import { useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";

export default function WorkspaceDropdown({ value = [], onChange, options = [], single = false }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef          = useRef(null);

  useEffect(() => {
    const fn = e => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const isSelected = value.length > 0;
  const filtered   = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

  const handleOpen = () => { setSearch(""); setOpen(prev => !prev); };

  const toggle = val => {
    if (single) {
      onChange(value.includes(val) ? [] : [val]);
      setOpen(false);
      return;
    }
    onChange(value.includes(val) ? value.filter(v => v !== val) : [...value, val]);
  };

  const clear = e => { e.stopPropagation(); onChange([]); };

  const summaryLabel = value.length === 1
    ? (options.find(o => o.value === value[0])?.label || value[0])
    : `${value.length} selected`;

  return (
    <div ref={wrapperRef} className="relative w-full">

      {/* ── Trigger ── */}
      <div
        onClick={handleOpen}
        className={`flex items-center gap-2 min-h-[34px] px-3 rounded-lg cursor-pointer bg-white select-none transition-all duration-150 border ${
          open ? "border-violet-500 ring-2 ring-violet-100" : "border-gray-200 shadow-sm hover:bg-gray-50"
        }`}
      >
        {!isSelected ? (
          <span className="text-xs text-slate-400 flex-1">Select Workspace</span>
        ) : (
          <span className="text-xs font-semibold text-slate-700 flex-1 truncate">{summaryLabel}</span>
        )}

        <div className="ml-auto flex items-center gap-1 shrink-0">
          {isSelected && (
            <span
              onClick={clear}
              className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs cursor-pointer hover:bg-red-50 hover:text-red-400 transition-colors"
            >
              ×
            </span>
          )}
          <ChevronDown size={12} className={`text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </div>

      {/* ── Dropdown ── */}
      {open && (
        <div className="absolute top-full left-0 mt-1.5 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xl w-full min-w-[220px] z-50">
          {options.length > 5 && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
              <Search size={12} className="text-slate-400 shrink-0" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search workspace..."
                className="flex-1 min-w-0 border-none outline-none text-xs text-slate-700 bg-transparent"
              />
            </div>
          )}
          <div className="py-1 max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-slate-400 text-center">No workspaces found</div>
            ) : filtered.map((opt, i) => {
              const checked = value.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors
                    ${checked ? "bg-violet-50" : "hover:bg-slate-50"}
                    ${i < filtered.length - 1 ? "border-b border-slate-50" : ""}`}
                >
                  <div
                    className={`w-4 h-4 rounded shrink-0 flex items-center justify-center border-2 transition-all ${
                      checked ? "border-violet-600 bg-violet-600" : "border-slate-300 bg-white"
                    }`}
                  >
                    {checked && <div className="w-1.5 h-1.5 rounded-sm bg-white" />}
                  </div>
                  <span className={`text-xs ${checked ? "font-bold text-violet-700" : "font-medium text-slate-700"}`}>
                    {opt.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
